/**
 * Configurare la runtime.
 *
 * Nimic din ce se încarcă aici nu e secret: sunt doar adrese publice. Tot ce
 * ține de broker (host, user, parolă MQTT) rămâne în backend — frontend-ul nu
 * vorbește niciodată direct cu brokerul, ci doar cu gateway-ul NestJS
 * autentificat. Vezi docs/backend-contract.md.
 *
 * Fișierul `public/config.json` e citit la boot, deci aceeași imagine de build
 * poate fi promovată între medii fără rebuild.
 */
export interface RuntimeConfig {
  /** Baza REST, ex. `/api` sau `https://meteo.example.ro/api`. */
  apiBaseUrl: string;
  /** Originea socket-ului; gol = aceeași origine cu aplicația. */
  socketUrl: string;
  /** Path-ul pe care ascultă gateway-ul socket.io din NestJS. */
  socketPath: string;
  /** Numele afișat în antet. */
  stationName: string;
  /**
   * Cheia de acces cu care aplicația cere un bilet, trimisă ca `X-Api-Key`.
   *
   * Nu e un secret și nici nu pretinde să fie: orice ajunge în browser se poate
   * citi din DevTools. E un **identificator revocabil** — dacă cineva o extrage
   * și abuzează, o revoci din admin și pui alta aici, fără să atingi celelalte
   * dispozitive. Cu un dashboard complet deschis n-ai fi avut ce revoca.
   *
   * Gol = nu se trimite niciun antet, iar dreptul de a primi bilet se decide
   * din rețea (util în LAN sau când backendul e pe `ACCESS_PUBLIC=1`).
   */
  accessKey: string;
  /** Date simulate local, fără backend. */
  demo: boolean;
}

const envFallback: RuntimeConfig = {
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? '/api',
  socketUrl: import.meta.env.VITE_SOCKET_URL ?? '',
  socketPath: import.meta.env.VITE_SOCKET_PATH ?? '/ws',
  stationName: import.meta.env.VITE_STATION_NAME ?? 'Valori meteo',
  accessKey: import.meta.env.VITE_ACCESS_KEY ?? '',
  demo: import.meta.env.VITE_DEMO === '1' || import.meta.env.VITE_DEMO === 'true',
};

let current: RuntimeConfig = envFallback;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Încarcă `public/config.json` peste fallback-ul din env. Nu aruncă. */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}config.json`, { cache: 'no-store' });
    if (!res.ok) return current;

    const raw: unknown = await res.json();
    if (!isRecord(raw)) return current;

    current = {
      apiBaseUrl: typeof raw.apiBaseUrl === 'string' ? raw.apiBaseUrl : envFallback.apiBaseUrl,
      socketUrl: typeof raw.socketUrl === 'string' ? raw.socketUrl : envFallback.socketUrl,
      socketPath: typeof raw.socketPath === 'string' ? raw.socketPath : envFallback.socketPath,
      stationName:
        typeof raw.stationName === 'string' && raw.stationName.trim() !== ''
          ? raw.stationName
          : envFallback.stationName,
      accessKey: typeof raw.accessKey === 'string' ? raw.accessKey.trim() : envFallback.accessKey,
      demo: typeof raw.demo === 'boolean' ? raw.demo : envFallback.demo,
    };
  } catch {
    // Fără config.json rămânem pe valorile din env.
  }
  return current;
}

export function getRuntimeConfig(): RuntimeConfig {
  return current;
}
